import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import "../../styles/clientPage.css";
import ClientStats from "../Client/ClientStats";
import ClientExecAttention from "./ClientExecAttention";
// import ProgressSpinner from "../Common/ProgressSpinner";
import ClientInteractionQuality from "./ClientInteractionQuality";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ClientService from "../../services/clients/ClientService";
import ClientHealthOverview from "./ClientHealthOverview";
import { ClientStatsType, StatConfig } from "../../utils/types";
import { getNormalizedTimeZone, getPresetDateRange, getTrailingWeeksUTC } from "../../utils/utils";
import useGlobalContext from "../../context/useGlobalContext";
import { Box } from "@mui/system";
import AppNotification from "../Common/AppNotification";
import TaskNotifications from "../ActionItems/TaskNotifications";
import HomeIcon from "../../assets/client/HomeIcon.svg";
import { useNotificationCount } from "../../hooks/useNotificationCount";
import { Typography } from "@mui/material";
import ClientAsk7G from "./ClientAsk7G";
import ClientPortfolioHealthTrendLine from "./ClientPortfolioHealthTrendLine";
import FilterPill from "../Common/FilterPill";
import OrgHierarcgyIcon from "../../assets/accounts/org-hierarchy-icon.svg";
import SpaceIcon from "../../assets/accounts/spaces-icon.svg";
import "../../styles/account.css";
import RightArrowIcon from "../../assets/common/right-arrow-icon.svg";

const ClientOverviewPage = () => {
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [clientStatsShowSpinner, setClientStatsShowSpinner] = useState<boolean>(false);
  const { showSnackbar } = useDisplaySnackbar();
  const [sortedClientStatsData, setFilteredClientStatsData] = useState<ClientStatsType[]>([]); // State to hold the sorted client stats data
  const location = useLocation();
  const navigate = useNavigate();
  const parentPageIdentifier="client-slideover";
  const {
    featureMap,
    spaceList,
    selectedView,
    setSelectedView,
    selectedSpace,
    setSelectedSpace,
    userTeamData,
    accountDetails,
  } = useGlobalContext();
  const clientPageEnabled = featureMap?.new_client_overview_page === true;
  const [businessStats, setBusinessStats] = useState<any>(null);

  const lastThreeMonths = useMemo(() => getPresetDateRange("last3MonthsFull"), []);
  const weeks = useMemo(() => getTrailingWeeksUTC(5), []);
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const isNotificationsEnabled = featureMap?.in_app_notifications === true;
  const userProfileOrgId = accountDetails?.profileOrgId ?? "";
  const userNotification = useNotificationCount(userProfileOrgId, isNotificationsEnabled);
  const Ask7GEnabled = featureMap?.business_ask7g === true;
  const [isAsk7GExpanded, setIsAsk7GExpanded] = useState(false);
  const isPortfolioHealthTrendEnabled = featureMap?.portfolio_health_trend === true;
  const [clientHealthTrendLineData, setClientHealthTrendLineData] = useState<any>(null);

  const [openSpacesDropdown, setOpenSpacesDropdown] = useState(false);
  const shareWithSpaceEnabled = featureMap?.share_with_space === true;
  const showViewSwitcher = shareWithSpaceEnabled && spaceList.length > 0 && userTeamData?.showTeamTab;
  const showSpacesFilterOnly = shareWithSpaceEnabled && spaceList.length > 0 && !userTeamData?.showTeamTab;
  const isSpaceMode = (showViewSwitcher ? selectedView === "spaces" : spaceList.length > 0) && selectedSpace.length > 0;

  const userDisplayName = useMemo(() => {
    const rootName = accountDetails?.name ?? "";
    return userTeamData?.showTeamTab
      ? `${rootName} & team`
      : rootName;
  }, [accountDetails?.name, userTeamData?.showTeamTab]);

  useEffect(() => {
    if (
      spaceList.length > 0 &&
      selectedView === "spaces" &&
      selectedSpace.length === 0 &&
      !openSpacesDropdown
    ) {
      setSelectedView("org");
      setOpenSpacesDropdown(false);
    }
  }, [selectedSpace, openSpacesDropdown, selectedView, spaceList]);
  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data: clientStatsData,
  } = ClientService.useGetClientStats({},false);

  const {
    isLoading: isWeekStatsLoading,
    isFetching: isWeekStatsFetching,
    isSuccess: isWeekStatsSuccess,
    isError: isWeekStatsError,
    error: weekStatsError,
    data: clientWeekStatsData,
  } = ClientService.useGetClientWeekStats(
    {
      week1StartTime: weeks.week1,
      week2StartTime: weeks.week2,
      week3StartTime: weeks.week3,
      week4StartTime: weeks.week4,
      week5StartTime: weeks.week5,
      timeZone: getNormalizedTimeZone(),
      isSpaceMode,
      spaceIds: isSpaceMode ? selectedSpace : undefined,
    },
    clientPageEnabled
  );

  const {
    isLoading: isBusinessClientsLoading,
    isFetching: isBusinessClientsFetching,
    isSuccess: isBusinessClientsSuccess,
    isError: isBusinessClientsError,
    error: isBusinessError,
    data: isBusinessClientsData
  } = ClientService.useGetClientBusinessStats({
    startTime: lastThreeMonths.start_time,
    endTime: lastThreeMonths.end_time,
    classificationType: "MAIN_CHAT",
    sortOrder: "",
    offset: 0,
    limit: 0,
    sortBy: "",
    employeePorId: loggedInProfileOrgId,
    timeZone: getNormalizedTimeZone(),
    isSpaceMode,
    spaceIds: isSpaceMode ? selectedSpace : undefined,
  }, !!lastThreeMonths.start_time && !!lastThreeMonths.end_time && clientPageEnabled)

  const {
    isLoading: isClientHealthTrendLoading,
    isFetching: isClientHealthTrendFetching,
    isSuccess: isClientHealthTrendSuccess,
    isError: isClientHealthTrendError,
    error: clientHealthTrendError,
    data: clientHealthTrendData,
  } = ClientService.useGetClientPortfolioHealthTrend(clientPageEnabled, isSpaceMode, isSpaceMode ? selectedSpace : undefined);

  useEffect(() => {
    if (location.search || location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);


  useEffect(() => {
    setClientStatsShowSpinner(isLoading || isFetching);
    if (isSuccess) {
      const results = [...(clientStatsData?.data || [])] as ClientStatsType[];
      // Sort by weekStart (newest first)
      results.sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime());
      setFilteredClientStatsData(results);
    }
    if (isError) {
      showSnackbar("error", error?.message || "Failed to fetch client stats");
    }
  }, [isLoading, isFetching, isSuccess, isError, error]);

  useEffect(() => {
    setClientStatsShowSpinner(isWeekStatsLoading || isWeekStatsFetching);
    if (isWeekStatsSuccess) {
      const results = [...(clientWeekStatsData?.data || [])] as ClientStatsType[];
      results.sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime());
      setFilteredClientStatsData(results);
    }
    if (isWeekStatsError) {
      showSnackbar("error", weekStatsError?.message || "Failed to fetch client stats");
    }
  }, [isWeekStatsLoading, isWeekStatsFetching, isWeekStatsSuccess, isWeekStatsError, weekStatsError]);
  useEffect(() =>{
    setShowSpinner(isBusinessClientsLoading || isBusinessClientsFetching)
    if (isBusinessClientsSuccess && isBusinessClientsData?.data) {
      setBusinessStats(isBusinessClientsData.data);
    } else if(isBusinessClientsError) {
      showSnackbar("error", isBusinessError.message)
    }
  }, [isBusinessClientsData, isBusinessClientsLoading, isBusinessClientsFetching, isBusinessClientsError, isBusinessError, isBusinessClientsSuccess]);  

  useEffect(() => {
    setShowSpinner(isClientHealthTrendLoading || isClientHealthTrendFetching);
    if(isClientHealthTrendSuccess && clientHealthTrendData?.data) {
      setClientHealthTrendLineData(clientHealthTrendData.data);
    } else if(isClientHealthTrendError) {
      showSnackbar("error", clientHealthTrendError.message || "Failed to fetch client portfolio health trend data");
    }
  }, [isClientHealthTrendLoading, isClientHealthTrendFetching, isClientHealthTrendSuccess, isClientHealthTrendError, clientHealthTrendError, clientHealthTrendData, showSnackbar]);

  const statConfigs:StatConfig[]  = [
    { key: "clientsMet", title: "Clients Met", valueKey: "totalClientsMet", type: "count" },
    { key: "clientsMrr", title: "Clients MRR", valueKey: "totalMrr", type: "currency" },
    { key: "analystTime", title: "Analyst Time", valueKey: "analystTime", type: "time" },
    { key: "managerTime", title: "Manager Time", valueKey: "managerTime", type: "time" },
  ];
  return (
    <Box
      id="meetingsParent"
      className="flex flex-col justify-start items-start gap-2 m-1"
      style={{ width: "auto" }}
    >
      <div className="interaction-breadcrumbs flex flex-row items-center w-full mt-2" style={{marginBottom: Ask7GEnabled ? "8px" : "0px"}}>
        <div className="flex flex-row justify-start items-center gap-2">
        <Box
          component={isAsk7GExpanded ? "button" : "div"}
          type={isAsk7GExpanded ? "button" : undefined}
          className={`flex flex-row justify-start items-center gap-2 ask7g-home-breadcrumb ${
            isAsk7GExpanded ? "ask7g-home-breadcrumb--expanded" : "ask7g-home-breadcrumb--collapsed"
          }`}
          onClick={isAsk7GExpanded ? () => setIsAsk7GExpanded(false) : undefined}
        >
          <img src={HomeIcon} alt="" className={`breadCrumbs-text ${isAsk7GExpanded ? "breadCrumbs-text--expanded" : "" }`} />
          <Typography className={`breadCrumbs-text ${isAsk7GExpanded ? "breadCrumbs-text--expanded" : "breadCrumbs-text--collapsed" }`} sx={{fontSize: "16px"}}>Home</Typography>
        </Box>
        {Ask7GEnabled && isAsk7GExpanded && (
          <>
            <img src={RightArrowIcon} alt="" style={{marginTop: "3px"}} />
            <Typography className="breadCrumbs-text ask7g-current-breadcrumb" sx={{fontSize: "16px"}}>
              Ask 7G
            </Typography>
          </>
        )}
        </div>
        {(showViewSwitcher || showSpacesFilterOnly) && (
          <div className="flex flex-row items-center gap-2 ml-4">
            {showViewSwitcher && (
              <Box className="switch-pill">
                <Box
                  className={`org-hierarchy-switch ${
                    selectedView === "org" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedView("org");
                  }}
                >
                  <img src={OrgHierarcgyIcon} alt="Org" />
                  <Typography className="switch-pill-text">Org Hierarchy</Typography>
                </Box>
                <Box
                  className={`org-hierarchy-switch ${
                    selectedView === "spaces" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedView("spaces");
                    if (selectedSpace.length === 0) {
                      setOpenSpacesDropdown(true);
                    }
                  }}
                >
                  <img src={SpaceIcon} alt="Spaces" />
                  <Typography className="switch-pill-text">Spaces</Typography>
                </Box>
              </Box>
            )}
            {(showSpacesFilterOnly || selectedView === "spaces") && spaceList.length > 0 && (
              <FilterPill
                label="Spaces"
                type="select"
                value={selectedSpace}
                options={spaceList.map((space) => ({
                  label: space.label,
                  value: space.value,
                  entityType: "space",
                }))}
                onChange={(value) => {
                  if (value) {
                    setOpenSpacesDropdown(false);
                  }
                  setSelectedSpace(value as string[]);
                }}
                showClearIcon={false}
                forceOpen={openSpacesDropdown}
                onOpenChange={setOpenSpacesDropdown}
                parent_page="client_overview_page"
              />
            )}
            {showViewSwitcher && selectedView === "org" && (
              <Box className="manager_name_box" style={{ cursor: "default" }}>
                <Typography className="manager_name_text">
                  {userDisplayName}
                </Typography>
              </Box>
            )}
          </div>
        )}
        <div className="ml-auto">
          {isNotificationsEnabled && (
            <AppNotification
            notificationCount= {userNotification.notificationCount}
            notificationApiRefetch = {userNotification.getNotificationCountRefetch}
            contentComponent={<TaskNotifications userNotification={userNotification}/>}
            />
          )}
        </div>
      </div>
      {Ask7GEnabled && (
        <ClientAsk7G
          isExpanded={isAsk7GExpanded}
          onToggleExpand={setIsAsk7GExpanded}
          isSpaceMode={isSpaceMode}
        />
      )}
      <div className="four-block-container">
        <div className="four-block-item">
        <ClientStats
          clientStatsData={clientWeekStatsData}
          statConfigs={statConfigs}
          showSpinner={clientStatsShowSpinner}
          parentPageIdentifier={parentPageIdentifier}
          headerTitle="Business Stats"
          timeRangeLabel="Trailing 4-weeks"
          slideOverParent="client-stats"
        />
        </div>
        <div className="four-block-item">
          <ClientHealthOverview ratedClientsData={businessStats} showSpinner={showSpinner} parentPageIdentifier={parentPageIdentifier}/>
        </div>
        <div className="four-block-item">
        {isPortfolioHealthTrendEnabled ?
          <ClientPortfolioHealthTrendLine clientHealthTrendLineData={clientHealthTrendLineData} showSpinner={showSpinner} />
        : <ClientInteractionQuality clientStatsData={sortedClientStatsData} showSpinner={clientStatsShowSpinner} parentPageIdentifier={parentPageIdentifier}/>
        }
        </div>
        <div className="four-block-item">
          <ClientExecAttention ratedClientsData={businessStats} showSpinner={showSpinner} parentPageIdentifier={parentPageIdentifier}/>
        </div>
      </div>
    </Box>
  );
};

export default ClientOverviewPage;
